import { useQueries } from '@tanstack/react-query';
import { Select, Spin } from 'antd';
import { Field, FieldArray, FieldProps, Form, Formik, FormikProps } from 'formik';
import { useMemo, useState } from 'react';

import { Button } from '../../../custom';
import {
  CreateMatricFormatPayload,
  MatricFormatSegmentPayload,
  useCreateMatricFormatMutation,
} from '../../../hooks/api/matricFormat/useCreateMatricFormatMutation';
import { useGetMatricFormatQuery } from '../../../hooks/api/matricFormat/useGetMatricFormatQuery';
import {
  PreviewMatricFormatPayload,
  usePreviewMatricFormatMutation,
} from '../../../hooks/api/matricFormat/usePreviewMatricFormatMutation';
import { getAllAcademicSession, getAllPrograms, getFaculty, getAllProgramType } from '../../../requests';
import { getAllProgram } from '../program/request';

import styles from './matricFormat.module.scss';

interface MatricFormatFormValues extends CreateMatricFormatPayload {
  sessionId: number;
  facultyId: number;
  programId: number;
  programTypeId: number;
}

// Must mirror the backend's MatricSegmentType enum values/names exactly.
const segmentTypeOptions = [
  { label: 'Year of Admission', value: 1 },
  { label: 'Faculty Code', value: 2 },
  { label: 'Department Code', value: 3 },
  { label: 'Programme Type', value: 4 },
  { label: 'Static Value', value: 5 },
  { label: 'Sequence Number', value: 6 },
];

const yearFormatOptions = [
  { label: 'YY', value: 'YY' },
  { label: 'YYYY', value: 'YYYY' },
];

const normalizeSegmentTypeKey = (value: string) => value.trim().toLowerCase().replace(/[\s_-]+/g, '');

const segmentTypeMap: Record<string, number> = {
  yearofadmission: 1,
  facultycode: 2,
  faculty: 2,
  programmecode: 3,
  programcode: 3,
  programmetypecode: 4,
  programtypecode: 4,
  staticvalue: 5,
  sequencenumber: 6,
  serialnumber: 6,
  sequence: 6,
};

const defaultSegments: MatricFormatSegmentPayload[] = [
  { order: 1, segmentType: 3 },
  { order: 2, segmentType: 5, staticValue: '/' },
  { order: 3, segmentType: 1, yearFormat: 'YY' },
  { order: 4, segmentType: 5, staticValue: '/' },
  { order: 5, segmentType: 6, padding: 4 },
];

const emptySegment = (order: number): MatricFormatSegmentPayload => ({
  order,
  segmentType: 5,
  staticValue: '/',
});

const toNumberSegmentType = (segmentType: MatricFormatSegmentPayload['segmentType']) => {
  if (typeof segmentType === 'number') {
    return Number.isNaN(segmentType) ? 0 : segmentType;
  }

  if (segmentType === null || typeof segmentType === 'undefined' || segmentType === '') {
    return 0;
  }

  const mapped = segmentTypeMap[normalizeSegmentTypeKey(String(segmentType))];

  if (typeof mapped !== 'undefined') {
    return mapped;
  }

  const numeric = Number(segmentType);

  return Number.isNaN(numeric) ? 0 : numeric;
};

const toSafeNumber = (value: unknown, fallback: number) => {
  if (value === null || typeof value === 'undefined' || value === '') {
    return fallback;
  }

  const numeric = Number(value);

  return Number.isNaN(numeric) ? fallback : numeric;
};

const normalizeSegments = (segments: MatricFormatSegmentPayload[]) => {
  return segments.map((segment, index) => {
    const segmentType = toNumberSegmentType(segment.segmentType);

    return {
      order: index + 1,
      segmentType,
      staticValue: segmentType === 5 ? segment.staticValue ?? '' : undefined,
      padding: segmentType === 6 ? toSafeNumber(segment.padding, 4) : undefined,
      yearFormat: segmentType === 1 ? segment.yearFormat ?? 'YY' : undefined,
    };
  });
};

const segmentTypeLabelMap = segmentTypeOptions.reduce<Record<number, string>>((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const describeSegment = (segment: ReturnType<typeof normalizeSegments>[number]) => {
  switch (segment.segmentType) {
    case 5:
      return segment.staticValue || '/';
    case 6:
      return '0'.repeat(segment.padding || 4);
    case 1:
      return segment.yearFormat || 'YY';
    default:
      return segmentTypeLabelMap[segment.segmentType] ?? '';
  }
};

const describeMatricFormat = (segments: MatricFormatSegmentPayload[]) =>
  normalizeSegments(segments)
    .map(describeSegment)
    .join('');

const getOptionLabel = (item: any) => item?.name ?? item?.programName ?? item?.program ?? item?.categoryName ?? item?.sessionName;

// Program rows from getAllProgram carry their own `id` (the program-detail
// record) plus the actual `programId` the API expects — prefer programId so
// the Program select submits the real program, not the detail row's id.
const getOptionValue = (item: any) => item?.programId ?? item?.id ?? item?.categoryId;

const toOptions = (items?: any[]) =>
  items
    ?.map((item) => ({
      label: getOptionLabel(item),
      value: getOptionValue(item),
    }))
    .filter((item) => item.label && typeof item.value !== 'undefined') ?? [];

const dedupeBy = (items: any[], keyFn: (item: any) => unknown) => {
  const seen = new Map<unknown, any>();

  items.forEach((item) => {
    const key = keyFn(item);

    if (!seen.has(key)) {
      seen.set(key, item);
    }
  });

  return Array.from(seen.values());
};

const SelectField = ({
  label,
  name,
  options,
  placeholder,
  loading,
  onAfterChange,
}: {
  label: string;
  name: keyof MatricFormatFormValues;
  options: { label: string; value: number | string }[];
  placeholder: string;
  loading?: boolean;
  onAfterChange?: (form: FormikProps<MatricFormatFormValues>) => void;
}) => (
  <Field name={name}>
    {({ form }: FieldProps<MatricFormatFormValues>) => (
      <div className={styles.field}>
        <label>{label}</label>
        <Select
          allowClear
          loading={loading}
          options={options}
          placeholder={placeholder}
          size="large"
          style={{ width: '100%' }}
          value={form.values[name] || undefined}
          onChange={(value) => {
            form.setFieldValue(name, value ?? 0);
            onAfterChange?.(form);
          }}
        />
      </div>
    )}
  </Field>
);

const MatricFormat = () => {
  const [previewValue, setPreviewValue] = useState('');
  const { matricFormatData, isLoading, isError, errorMessage } = useGetMatricFormatQuery();
  const { createMatricFormatHandler, isCreateLoading } = useCreateMatricFormatMutation();
  const { previewMatricFormatHandler, isPreviewLoading } = usePreviewMatricFormatMutation();

  const [sessionsQuery, facultiesQuery, programsQuery, programTypesQuery, programDetailsQuery] = useQueries({
    queries: [
      { queryKey: ['getAll-sessions', 'matric-format'], queryFn: () => getAllAcademicSession(1, 100) },
      { queryKey: ['get-faculty', 'matric-format'], queryFn: () => getFaculty(1, 100) },
      { queryKey: ['get-all-programs', 'matric-format'], queryFn: () => getAllPrograms(1, 100) },
      { queryKey: ['get-program-types', 'matric-format'], queryFn: getAllProgramType },
      { queryKey: ['get-program-details', 'matric-format'], queryFn: () => getAllProgram(1, 100) },
    ],
  });

  const initialValues = useMemo<MatricFormatFormValues>(() => {
    const existingSegments = matricFormatData?.segments?.length
      ? matricFormatData.segments.map((segment) => ({
          order: segment.order,
          segmentType: toNumberSegmentType(segment.segmentType),
          staticValue: segment.staticValue && segment.staticValue !== 'string' ? segment.staticValue : '',
          padding: segment.padding,
          yearFormat: segment.yearFormat && segment.yearFormat !== 'string' ? segment.yearFormat : 'YY',
        }))
      : defaultSegments;

    return {
      segments: normalizeSegments(existingSegments),
      sessionId: 0,
      facultyId: 0,
      programId: 0,
      programTypeId: 0,
    };
  }, [matricFormatData]);

  const isAlreadyConfigured = Boolean(matricFormatData?.segments?.length);
  const existingFormatPreview = isAlreadyConfigured ? describeMatricFormat(matricFormatData!.segments) : '';

  const sessionOptions = toOptions(sessionsQuery.data?.data);
  const facultyOptions = toOptions(facultiesQuery.data?.data);
  const programOptions = toOptions(programDetailsQuery.data?.data ?? programsQuery.data?.data);
  const programTypeOptions = toOptions(programTypesQuery.data?.data);

  const handlePreview = async (values: MatricFormatFormValues) => {
    const payload: PreviewMatricFormatPayload = {
      segments: normalizeSegments(values.segments),
      sessionId: Number(values.sessionId ?? 0),
      facultyId: Number(values.facultyId ?? 0),
      programId: Number(values.programId ?? 0),
      programTypeId: Number(values.programTypeId ?? 0),
    };

    const response = await previewMatricFormatHandler(payload);

    if (response?.data) {
      setPreviewValue(response.data);
    }
  };

  const handleSubmit = (values: MatricFormatFormValues) => {
    createMatricFormatHandler({ segments: normalizeSegments(values.segments) });
  };

  if (isLoading) {
    return <Spin size="large" />;
  }

  if (isError) {
    return <div>Error: {errorMessage}</div>;
  }

  return (
    <main>
      <section className={styles.pageHeader}>
        <div>
          <h3>Matric Format</h3>
        </div>
      </section>

      {isAlreadyConfigured && (
        <section className={styles.configuredBanner}>
          <div>
            <strong>Matric format already configured</strong>
            <p>
              A format is already set up for this institution — no need to create a new one. You can review it below and
              update it here if the structure needs to change.
            </p>
          </div>
          <div className={styles.configuredPattern}>{existingFormatPreview}</div>
        </section>
      )}

      <Formik initialValues={initialValues} onSubmit={(values) => handleSubmit(values)} enableReinitialize>
        {({ values, setFieldValue }: FormikProps<MatricFormatFormValues>) => {
          const programRows = programDetailsQuery.data?.data ?? [];

          const selectedFaculty = facultiesQuery.data?.data?.find(
            (faculty: any) => getOptionValue(faculty) === values.facultyId,
          );

          const facultyScopedProgramRows = selectedFaculty
            ? programRows.filter((row: any) => row?.categoryCode === selectedFaculty?.categoryCode)
            : programRows;

          const scopedProgramOptions = facultyScopedProgramRows.length
            ? toOptions(dedupeBy(facultyScopedProgramRows, (row: any) => row.programId))
            : programOptions;

          const selectedProgramRows = programRows.filter((row: any) => row.programId === values.programId);

          const scopedProgramTypeOptions = selectedProgramRows.length
            ? toOptions(
                dedupeBy(selectedProgramRows, (row: any) => row.programTypeId).map((row: any) => ({
                  id: row.programTypeId,
                  name: row.programTypeName,
                })),
              )
            : programTypeOptions;

          return (
          <Form>
            <section className={styles.layout}>
              <div className={styles.builder}>
                <div className={styles.builderHeader}>
                  <h4>Segments</h4>
                  <Button
                    type="button"
                    text="Add Segment"
                    onClick={() => setFieldValue('segments', [...values.segments, emptySegment(values.segments.length + 1)])}
                  />
                </div>

                <FieldArray name="segments">
                  {({ remove, move }) => (
                    <div className={styles.segmentList}>
                      {values.segments.map((segment, index) => {
                        const typeName = `segments.${index}.segmentType`;
                        const segmentType = toNumberSegmentType(segment.segmentType);

                        return (
                          <div className={styles.segmentCard} key={`${index}-${segment.order}`}>
                            <div className={styles.segmentTop}>
                              <span className={styles.segmentOrder}>#{index + 1}</span>
                              <div className={styles.segmentActions}>
                                <Button
                                  type="button"
                                  variant="text"
                                  text="Up"
                                  disabled={index === 0}
                                  onClick={() => move(index, index - 1)}
                                />
                                <Button
                                  type="button"
                                  variant="text"
                                  text="Down"
                                  disabled={index === values.segments.length - 1}
                                  onClick={() => move(index, index + 1)}
                                />
                                <Button
                                  type="button"
                                  variant="text"
                                  text="Remove"
                                  disabled={values.segments.length === 1}
                                  onClick={() => remove(index)}
                                />
                              </div>
                            </div>

                            <div className={styles.segmentGrid}>
                              <div className={styles.field}>
                                <label>Segment Type</label>
                                <Select
                                  options={segmentTypeOptions}
                                  placeholder="Select segment type"
                                  size="large"
                                  value={segmentType || undefined}
                                  onChange={(value) => {
                                    setFieldValue(typeName, value);
                                    setFieldValue(`segments.${index}.staticValue`, value === 5 ? segment.staticValue ?? '/' : undefined);
                                    setFieldValue(`segments.${index}.padding`, value === 6 ? segment.padding ?? 4 : undefined);
                                    setFieldValue(`segments.${index}.yearFormat`, value === 1 ? segment.yearFormat ?? 'YY' : undefined);
                                  }}
                                />
                              </div>

                              {segmentType === 5 && (
                                <Field name={`segments.${index}.staticValue`}>
                                  {({ field }: FieldProps) => (
                                    <div className={styles.field}>
                                      <label>Static Value</label>
                                      <input {...field} className={styles.input} placeholder="/" />
                                    </div>
                                  )}
                                </Field>
                              )}

                              {segmentType === 1 && (
                                <div className={styles.field}>
                                  <label>Year Format</label>
                                  <Select
                                    options={yearFormatOptions}
                                    size="large"
                                    value={segment.yearFormat || 'YY'}
                                    onChange={(value) => setFieldValue(`segments.${index}.yearFormat`, value)}
                                  />
                                </div>
                              )}

                              {segmentType === 6 && (
                                <Field name={`segments.${index}.padding`}>
                                  {({ field }: FieldProps) => (
                                    <div className={styles.field}>
                                      <label>Padding</label>
                                      <input {...field} className={styles.input} min={1} placeholder="4" type="number" />
                                    </div>
                                  )}
                                </Field>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </FieldArray>
              </div>

              <aside className={styles.previewPanel}>
                <div className={styles.panelHeader}>
                  <h4>Preview</h4>
                </div>

                <div className={styles.formatPreview}>
                  {previewValue || <span className={styles.muted}>Preview appears here</span>}
                </div>

                <div className={styles.contextGrid}>
                  <SelectField
                    label="Session"
                    name="sessionId"
                    options={sessionOptions}
                    placeholder="Select session"
                    loading={sessionsQuery.isLoading}
                  />
                  <SelectField
                    label="Faculty"
                    name="facultyId"
                    options={facultyOptions}
                    placeholder="Select faculty"
                    loading={facultiesQuery.isLoading}
                    onAfterChange={(form) => {
                      form.setFieldValue('programId', 0);
                      form.setFieldValue('programTypeId', 0);
                    }}
                  />
                  <SelectField
                    label="Program"
                    name="programId"
                    options={scopedProgramOptions}
                    placeholder="Select program"
                    loading={programsQuery.isLoading || programDetailsQuery.isLoading}
                    onAfterChange={(form) => form.setFieldValue('programTypeId', 0)}
                  />
                  <SelectField
                    label="Program Type"
                    name="programTypeId"
                    options={scopedProgramTypeOptions}
                    placeholder="Select program type"
                    loading={programTypesQuery.isLoading}
                  />
                </div>

                <div className={styles.previewActions}>
                  <Button
                    type="button"
                    variant="text"
                    text={isPreviewLoading ? 'Generating...' : 'Generate Preview'}
                    isLoading={isPreviewLoading}
                    disabled={isPreviewLoading}
                    onClick={() => handlePreview(values)}
                  />
                </div>
              </aside>
            </section>

            <div className={styles.actions}>
              <Button
                type="submit"
                text={isCreateLoading ? 'Saving...' : isAlreadyConfigured ? 'Update Matric Format' : 'Save Matric Format'}
                isLoading={isCreateLoading}
                disabled={isCreateLoading}
              />
            </div>
          </Form>
          );
        }}
      </Formik>
    </main>
  );
};

export default MatricFormat;
